"use client";

import { motion } from "framer-motion";
import heroImg from "@/assets/hero-living-room.jpg";
import { Nav } from "@/components/site/Nav";
import { ArrowRight } from "lucide-react";
import { CountUp } from "../CountUp";

export function Hero() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.21, 0.47, 0.32, 0.98],
      },
    },
  };

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <section className="relative min-h-screen w-full overflow-hidden">
      <img
        src={heroImg.src}
        alt="Modern minimalist living room with the Apollo G3 smart hub"
        width={1920}
        height={1280}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/70 to-transparent" />
      <Nav />

      <motion.div
        className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-40 pb-24 lg:pt-56 lg:pb-40"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-2xl">
          <motion.p
            variants={itemVariants}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-foreground/60 mb-8"
          >
            <span className="h-px w-8 bg-foreground/40" />
            Introducing Apollo G3
          </motion.p>

          <motion.h1
            variants={itemVariants}
            className="text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight text-foreground"
          >
            The center<br />of your home.
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="mt-8 max-w-lg text-lg text-foreground/70 leading-relaxed"
          >
            One device to orchestrate every light, lock, sound and screen. Designed
            in California. Engineered to disappear.
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <a
              href="#pricing"
              onClick={(e) => scrollToSection(e, "pricing")}
              className="inline-flex items-center gap-2 rounded-full bg-[#0055FF] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#0044DD] transition shadow-lg shadow-blue-500/20"
            >
              Order Now <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#showcase"
              onClick={(e) => scrollToSection(e, "showcase")}
              className="inline-flex items-center gap-2 rounded-full border border-foreground/15 px-7 py-3.5 text-sm font-medium text-foreground hover:bg-foreground/5 transition"
            >
              Watch the film
            </a>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="mt-16 flex items-center gap-8 text-xs text-foreground/60"
          >
            <div>
              <div className="text-2xl font-semibold text-foreground"><CountUp value={`4.9`} /> </div>
              <div>2,300+ reviews</div>
            </div>
            <div className="h-10 w-px bg-foreground/15" />
            <div>
              <div className="text-2xl font-semibold text-foreground">Free</div>
              <div>2-day shipping</div>
            </div>
            <div className="h-10 w-px bg-foreground/15" />
            <div>
              <div className="text-2xl font-semibold text-foreground"><CountUp value={`90`} /> day</div>
              <div>Home trial</div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
